import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ReactRouter from 'react-router-dom'
import PropTypes from 'prop-types'

import * as Common from '@rushplay/common'
import * as Forms from '@rushplay/forms'
import * as Processes from '@rushplay/processes'
import * as Notifications from '@rushplay/notifications'
import * as Herz from '@rushplay/herz'

import * as icons from './icons'
import { EmailPattern, ProcessesIds } from './constants'
import { InputField } from './input-field'
import { InputPasswordField } from './input-password-field'
import { LoadingNotificationWrapper } from './loading-notification-wrapper'
import { SubmitButton } from './submit-button'
import { useLogin } from './use-login'

const dataSchema = {
  type: 'object',
  properties: {
    email: {
      type: 'string',
      pattern: EmailPattern,
    },
    password: {
      type: 'string',
    },
  },
  required: ['email', 'password'],
}

const formName = 'login'
const preloadSchemaKeys = Forms.findTranslationKeys(formName, dataSchema, [
  'label',
  'placeholder',
])

export function LoginForm(props) {
  const translate = Herz.I18n.useTranslate()
  const dispatch = ReactRedux.useDispatch()
  const session = Herz.Auth.useSession()
  const { handleLogin, handleLoginError } = useLogin()

  const loading = ReactRedux.useSelector(state =>
    Processes.isRunning(state.processes, {
      ids: [ProcessesIds.LOGIN_REQUEST],
    })
  )

  function handleSubmit(username, password) {
    dispatch([
      Processes.start(ProcessesIds.LOGIN_REQUEST),
      Notifications.dismissAll(),
    ])

    session
      .create({
        username,
        password,
      })
      .then(response => {
        const token = response.createSession?.token

        if (token) {
          handleLogin(token)
          return
        }
        if (response?.error) {
          handleLoginError(response.error)
          return
        }
        throw new Error('Response without token and error message')
      })
      .catch(error => handleLoginError(error))
  }

  return (
    <Common.Box borderRadius={1} width="100%">
      <Forms.Provider
        name={formName}
        schema={dataSchema}
        onSubmit={(formErrors, data) => {
          if (
            Object.keys(formErrors).length === 0 &&
            formErrors.constructor === Object
          ) {
            handleSubmit(data.email, data.password)
          }
        }}
      >
        <Common.Box display="grid" gridGap="0" pb={0}>
          <InputField
            labelFontColor={props.labelFontColor}
            autoComplete="email"
            inputMode="email"
            prependIcon={<icons.Email />}
            scope="#/properties/email"
          />
          <InputPasswordField
            autoComplete="password"
            labelFontColor={props.labelFontColor}
            prependIcon={<icons.Lock />}
            scope="#/properties/password"
          />
        </Common.Box>
        <Common.Box
          fontSize="12px"
          textAlign="right"
          textDecoration="underline"
          color="header-text"
        >
          <ReactRouter.Link
            data-testid="login-password-recovery"
            to="/password-recovery"
          >
            <Common.Text color="page-nav-blue" textDecoration="underline">
              {translate('forgot-password')}
            </Common.Text>
          </ReactRouter.Link>
        </Common.Box>
        <Common.Box width="100%" pt={3} textAlign="center">
          <LoadingNotificationWrapper shouldDisplay={loading} name={formName}>
            <SubmitButton stretch loading={loading} spinner>
              {translate('login.submit.label')}
            </SubmitButton>
          </LoadingNotificationWrapper>
        </Common.Box>
      </Forms.Provider>
    </Common.Box>
  )
}

Herz.I18n.Loader.preload(
  ['forgot-password', 'login.submit.label', ...preloadSchemaKeys],
  LoginForm
)

LoginForm.propTypes = {
  labelFontColor: PropTypes.string,
}

// for @loadable/components
export default LoginForm
